import {
	getCachedImsAccessToken,
	getProxyAuthHeaders,
} from '@/auth/imsTokenStorage';
import { config } from '@/config/env';
import { logger } from '@/logger';

import {
	type BackendGenerateRequest,
	type BackendGenerateResult,
	DEFAULT_ENABLED_TOOLS,
	type FireflyEditorEnvironment,
	type FireflyImageEditorConfig,
	type FireflyTool,
} from './types';

/**
 * Maps Bridgeway's environment (`local | dev | stage | prod`) to the Firefly
 * editor's Colligo environment (`production | stage | development`). `local` and
 * `dev` both map to Colligo `development`, `stage` maps to `stage`, and only
 * `prod` hits Colligo `production`.
 */
export function mapBeaconEnvToFireflyEnv(
	beaconEnv: typeof config.ENV,
): FireflyEditorEnvironment {
	switch (beaconEnv) {
		case 'prod':
			return 'production';
		case 'stage':
			return 'stage';
		case 'dev':
		case 'local':
			return 'development';
	}
}

interface BuildEditorConfigArgs {
	container: HTMLElement;
	/**
	 * Live token getter. Defaults to reading `localStorage` so a mid-session
	 * refresh is always reflected. Tests inject a fake. We intentionally do NOT
	 * capture a token string up front — capturing would go stale within the
	 * ~24h TTL and silently 401 mid-edit.
	 */
	getAccessToken?: () => string | null;
	enabledTools?: readonly FireflyTool[];
	beaconEnv?: typeof config.ENV;
	/**
	 * Current user id. When provided, PROMPT generation is routed through the
	 * Bridgeway backend Firefly route (which holds the `firefly_api` service token);
	 * omit to leave prompt generation on the direct-Colligo path.
	 */
	userId?: string | null;
}

/**
 * Builds the PROMPT-generation callback that POSTs the editor's edited bytes to
 * Bridgeway's backend Firefly route. The route authenticates the user with the
 * browser token (Authorization / x-user-token) and calls Firefly with its own
 * service token — the service token never reaches the browser.
 */
function buildGenerateImageViaBackend(
	userId: string,
	getAccessToken: () => string | null,
): (req: BackendGenerateRequest) => Promise<BackendGenerateResult> {
	return async (req) => {
		const url = `${config.BRIDGEWAY_API_URL}/api/user/${encodeURIComponent(
			userId,
		)}/firefly/generate-image`;
		const res = await fetch(url, {
			method: 'POST',
			headers: {
				...getProxyAuthHeaders(getAccessToken()),
				'Content-Type': 'application/json',
			},
			body: JSON.stringify({
				prompt: req.prompt,
				reference_image_base64: req.referenceImageBase64,
				reference_image_mime: req.referenceImageMime,
				extra_reference_images: (req.extraReferences ?? []).map((r) => ({
					image_base64: r.imageBase64,
					mime: r.mime,
				})),
				width: req.size?.width,
				height: req.size?.height,
			}),
		});
		if (!res.ok) {
			// The route answers a rejected prompt with a 422 whose `detail` names the
			// cause and the fix. The editor bundle owns the error UI and shows only
			// the thrown message, so that message is the one place the cause can
			// reach the customer. A body that is missing or is not JSON leaves the
			// status-only message, which is all a 502 ever carries.
			let detail: string | undefined;
			try {
				const body = (await res.json()) as { detail?: unknown };
				// An empty string is a valid `detail` per the type but carries no cause,
				// so it is treated the same as a missing one rather than logged as if
				// meaningful.
				detail =
					typeof body.detail === 'string' && body.detail
						? body.detail
						: undefined;
			} catch {
				detail = undefined;
			}
			logger.error('firefly generate-image failed', {
				status: res.status,
				detail,
			});
			throw new Error(
				detail
					? `firefly generate-image failed: ${res.status} — ${detail}`
					: `firefly generate-image failed: ${res.status}`,
			);
		}
		const json = (await res.json()) as { image_base64: string; mime: string };
		return { imageBase64: json.image_base64, mime: json.mime };
	};
}

/**
 * Builds the {@link FireflyImageEditorConfig} the loader needs from Bridgeway's
 * auth + env. This is where Bridgeway's "no paywall" posture is enforced:
 * the loader uses this config to construct a host with a NO-OP `commerceData`
 * (so no Adobe commerce dialog can ever open) and `hideSignInDialog` (Bridgeway
 * owns auth). `enabledTools` defaults to the four proven-free tools — upscale is
 * never enabled (it 403s on a Bridgeway token).
 */
export function buildFireflyEditorConfig({
	container,
	getAccessToken = getCachedImsAccessToken,
	enabledTools = DEFAULT_ENABLED_TOOLS,
	beaconEnv = config.ENV,
	userId,
}: BuildEditorConfigArgs): FireflyImageEditorConfig {
	return {
		container,
		getAccessToken,
		environment: mapBeaconEnvToFireflyEnv(beaconEnv),
		enabledTools,
		generateImageViaBackend: userId
			? buildGenerateImageViaBackend(userId, getAccessToken)
			: undefined,
	};
}
